import { getBatchSegmentKey, getProgressRangeForBatches } from '@/lib/progress';
import { useCallback, useEffect, useRef, useState } from 'react';

const BATCH_TICK_MS = 200;
const BATCH_SMOOTH_MS = 3200;
const BATCH_FRAC_CAP = 0.88;

export interface SimulatedProgressBatchDrive {
  enabled: boolean;
  jobProgress: number | null;
  currentBatch: number | null;
  totalBatches: number | null;
  totalLength: number | null;
}

interface UseSimulatedProgressOptions {
  ceiling?: number;
  intervalMs?: number;
  completionDelayMs?: number;
  batchDrive?: SimulatedProgressBatchDrive;
}

interface UseSimulatedProgressReturn {
  progress: number | null;
  start: () => void;
  startControlled: () => void;
  // eslint-disable-next-line no-unused-vars
  setProgressValue: (value: number) => void;
  // eslint-disable-next-line no-unused-vars
  finish: (onDone?: () => void) => void;
  cancel: () => void;
  // eslint-disable-next-line no-unused-vars
  run: <T>(fn: () => Promise<T>, onDone?: () => void) => Promise<T>;
}

export function useSimulatedProgress({
  ceiling = 85,
  intervalMs = 600,
  completionDelayMs = 600,
  batchDrive,
}: UseSimulatedProgressOptions = {}): UseSimulatedProgressReturn {
  const [progress, setProgress] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const batchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearBatchSmoothing = useCallback(() => {
    if (batchIntervalRef.current) {
      clearInterval(batchIntervalRef.current);
      batchIntervalRef.current = null;
    }
  }, []);

  const clearTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    clearBatchSmoothing();
  }, [clearBatchSmoothing]);

  const batchKeySeenRef = useRef<string | null>(null);
  const batchSinceRef = useRef(0);
  const finishingRef = useRef(false);
  const batchDriveRef = useRef(batchDrive);
  batchDriveRef.current = batchDrive;

  const bdEnabled = batchDrive?.enabled ?? false;
  const bdJobProgress = batchDrive?.jobProgress ?? null;
  const bdCurrentBatch = batchDrive?.currentBatch ?? null;
  const bdTotalBatches = batchDrive?.totalBatches ?? null;
  const bdTotalLength = batchDrive?.totalLength ?? null;

  useEffect(() => {
    if (!bdEnabled) {
      clearBatchSmoothing();
      batchKeySeenRef.current = null;
      return;
    }

    if (finishingRef.current) return;

    if (bdJobProgress === null) {
      clearBatchSmoothing();
      return;
    }

    const range = getProgressRangeForBatches(bdCurrentBatch, bdTotalBatches, bdTotalLength);
    const k = getBatchSegmentKey(bdCurrentBatch, bdTotalBatches, bdTotalLength);

    if (k !== batchKeySeenRef.current) {
      batchKeySeenRef.current = k;
      batchSinceRef.current = Date.now();
      if (range) {
        const seed = Math.max(bdJobProgress, range.low);
        setProgress(Math.min(seed, 99.5));
      } else {
        setProgress(bdJobProgress);
      }
    }

    const tick = () => {
      if (finishingRef.current) return;
      const batchDrive = batchDriveRef.current;
      if (!batchDrive?.enabled || batchDrive.jobProgress === null) return;

      const jobProgress = batchDrive.jobProgress;
      const range = getProgressRangeForBatches(
        batchDrive.currentBatch,
        batchDrive.totalBatches,
        batchDrive.totalLength,
      );

      if (!range) {
        setProgress(jobProgress);
        return;
      }

      const { low, high } = range;
      const span = Math.max(high - low, 1e-6);
      const elapsed = Date.now() - batchSinceRef.current;
      const frac = Math.min(BATCH_FRAC_CAP, elapsed / BATCH_SMOOTH_MS);
      const simulated = low + span * frac;
      const softCap = high - span * 0.02;

      const next = Math.min(99.5, Math.max(jobProgress, simulated, low), softCap);

      setProgress((prev) => (prev == null ? next : Math.max(prev, next)));
    };

    tick();
    batchIntervalRef.current = setInterval(tick, BATCH_TICK_MS);

    return () => {
      clearBatchSmoothing();
    };
  }, [
    bdEnabled,
    bdJobProgress,
    bdCurrentBatch,
    bdTotalBatches,
    bdTotalLength,
    clearBatchSmoothing,
  ]);

  const start = useCallback(() => {
    clearTick();
    batchKeySeenRef.current = null;
    setProgress(0);
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev === null || prev >= ceiling) return prev;
        const increment = Math.max(0.3, (ceiling - prev) / 25);
        return Math.min(ceiling, prev + increment);
      });
    }, intervalMs);
  }, [ceiling, intervalMs, clearTick]);

  const startControlled = useCallback(() => {
    clearTick();
    batchKeySeenRef.current = null;
    setProgress(0);
  }, [clearTick]);

  const setProgressValue = useCallback((value: number) => {
    setProgress(Math.max(0, Math.min(100, value)));
  }, []);

  const finish = useCallback(
    (onDone?: () => void) => {
      clearTick();
      batchKeySeenRef.current = null;
      finishingRef.current = true;
      setProgress(100);
      setTimeout(() => {
        setProgress(null);
        finishingRef.current = false;
        onDone?.();
      }, completionDelayMs);
    },
    [clearTick, completionDelayMs],
  );

  const cancel = useCallback(() => {
    clearTick();
    batchKeySeenRef.current = null;
    finishingRef.current = false;
    setProgress(null);
  }, [clearTick]);

  const run = useCallback(
    async <T>(fn: () => Promise<T>, onDone?: () => void): Promise<T> => {
      start();
      try {
        const result = await fn();
        finish(onDone);
        return result;
      } catch (error) {
        cancel();
        throw error;
      }
    },
    [start, finish, cancel],
  );

  return { progress, start, startControlled, setProgressValue, finish, cancel, run };
}
